import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import ThemeChip from "./ThemeChip";
import { useFilters } from "../context/FilterContext";
import { buildDashboardParams } from "../utils/dashboardFilters";
import { SectionHeader } from "./SectionHeader";

const PIE_COLORS = ["#B71C1C", "#16A34A", "#F59E0B", "#64748B", "#8B5CF6"];

const BUCKETS = [
  { label: "< 3 Cr",        col: "< 3 Cr",        test: a => a < 3 },
  { label: "3 – 5 Cr",      col: "3–5 Cr",        test: a => a >= 3 && a <= 5 },
  { label: "5 – 10 Cr",     col: "5–10 Cr",       test: a => a > 5 && a <= 10 },
  { label: "10 – 15 Cr",    col: "10–15 Cr",      test: a => a > 10 && a <= 15 },
  { label: "15 Cr & above", col: "15 Cr & above", test: a => a > 15 },
];

export default function ThemeSanctionMatrix() {
  const { filters, viewMode, API } = useFilters();
  const params = buildDashboardParams(filters, { view_mode: viewMode });
  const [themeAmounts, setThemeAmounts] = useState({});

  useEffect(() => {
    axios.get(`${API}/financial/theme-sanction-matrix`, { params })
      .then(r => setThemeAmounts(r.data?.theme_amounts || {}))
      .catch(() => setThemeAmounts({}));
  }, [API, JSON.stringify(params)]);

  const { matrix, themes, colTotals, rowTotals, grandTotal, maxVal, pieData, pieTotal } = useMemo(() => {
    const themes = Object.keys(themeAmounts).sort();
    const matrix = {};
    const colTotals = BUCKETS.map(() => 0);
    const rowTotals = {};
    let grandTotal = 0;
    let maxVal = 0;
    const allAmounts = [];

    for (const theme of themes) {
      const amounts = themeAmounts[theme] || [];
      allAmounts.push(...amounts);
      matrix[theme] = BUCKETS.map((b, i) => {
        const count = amounts.filter(b.test).length;
        colTotals[i] += count;
        return count;
      });
      const rowTotal = amounts.length;
      rowTotals[theme] = rowTotal;
      grandTotal += rowTotal;
      maxVal = Math.max(maxVal, ...matrix[theme]);
    }

    const pieData = BUCKETS.map((b, i) => ({
      name: b.label,
      value: allAmounts.filter(b.test).length,
    }));
    const pieTotal = pieData.reduce((s, d) => s + d.value, 0);

    return { matrix, themes, colTotals, rowTotals, grandTotal, maxVal, pieData, pieTotal };
  }, [themeAmounts]);

  if (themes.length === 0) return null;

  return (
    <SectionHeader title="Theme-wise Grant Size Distribution" subtitle="Grant count by sanctioned amount ranges" collapsible defaultOpen={true}>
      <div className="flex gap-4" data-testid="theme-sanction-matrix">
        {/* Left: Heatmap table */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="sanction-matrix-table">
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="text-left text-slate-500 font-medium py-2 pr-2">Theme</th>
                {BUCKETS.map((b, i) => (
                  <th key={i} className="text-center text-slate-600 font-semibold py-2 px-2 min-w-[60px]">{b.col}</th>
                ))}
                <th className="text-center text-slate-800 font-bold py-2 px-2 min-w-[50px] bg-slate-50">Total</th>
              </tr>
            </thead>
            <tbody>
              {themes.map((theme, ti) => (
                <tr key={ti} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="py-1.5 pr-2"><ThemeChip theme={theme} /></td>
                  {matrix[theme].map((count, ci) => {
                    const intensity = count > 0 && maxVal > 0 ? Math.max(0.08, count / maxVal) : 0;
                    return (
                      <td key={ci} className="py-1.5 px-2 text-center text-[11px] rounded-sm"
                        style={{
                          backgroundColor: count > 0 ? `rgba(183,28,28,${intensity})` : "transparent",
                          color: intensity > 0.55 ? "#fff" : intensity > 0 ? "#B71C1C" : "#CBD5E1"
                        }}>
                        <span className="font-semibold">{count > 0 ? count : "—"}</span>
                      </td>
                    );
                  })}
                  <td className="py-1.5 px-2 text-center text-[11px] font-bold text-slate-800 bg-slate-50">
                    {rowTotals[theme]}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50">
                <td className="py-2 pr-2 font-bold text-slate-800 text-[11px]">Total</td>
                {colTotals.map((t, i) => (
                  <td key={i} className="py-2 px-2 text-center text-[11px] font-bold text-slate-800">{t}</td>
                ))}
                <td className="py-2 px-2 text-center text-[11px] font-extrabold text-slate-900">{grandTotal}</td>
              </tr>
            </tfoot>
          </table>
          {/* Legend */}
          <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-600">
            <span className="font-medium">Intensity:</span>
            {[0.1, 0.35, 0.65, 1].map((o, i) => (
              <span key={o} className="flex items-center gap-0.5">
                <span className="w-4 h-3 rounded-sm inline-block" style={{ backgroundColor: `rgba(183,28,28,${o})` }} />
                {["Low", "Med", "High", "Max"][i]}
              </span>
            ))}
          </div>
        </div>

        {/* Right: Pie chart */}
        {pieTotal > 0 && (
          <div className="flex-shrink-0 w-[300px] flex flex-col items-center" data-testid="grant-size-pie">
            <h4 className="text-xs font-semibold text-slate-700 mb-3 self-start">Grant Size Distribution</h4>
            <PieChart width={240} height={240}>
              <Pie data={pieData} cx={120} cy={120} innerRadius={55} outerRadius={105} dataKey="value" paddingAngle={2} strokeWidth={0}>
                {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
              </Pie>
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0];
                return (
                  <div className="bg-slate-800 text-white text-sm rounded px-3 py-2 shadow-lg font-medium">
                    {d.name}: {d.value} ({pieTotal > 0 ? Math.round(d.value / pieTotal * 100) : 0}%)
                  </div>
                );
              }} />
            </PieChart>
            <div className="flex flex-col gap-2 mt-3 w-full">
              {pieData.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i] }} />
                  <span className="text-[11px] text-slate-700 font-medium flex-1">{d.name}</span>
                  <span className="text-[11px] font-bold text-slate-900 w-8 text-right">{d.value}</span>
                  <span className="text-[10px] text-slate-500 w-10">({pieTotal > 0 ? Math.round(d.value / pieTotal * 100) : 0}%)</span>
                </div>
              ))}
              <div className="flex items-center gap-2 border-t border-slate-200 pt-2 mt-0.5">
                <span className="w-3 h-3 flex-shrink-0 invisible" />
                <span className="text-[11px] text-slate-700 font-medium flex-1">Total</span>
                <span className="text-[11px] font-extrabold text-slate-900 w-8 text-right">{pieTotal}</span>
                <span className="w-10" />
              </div>
            </div>
          </div>
        )}
      </div>
    </SectionHeader>
  );
}
